import {
  budgetCategorySchema,
  eventBudgetSchema,
  FINANCE_STATUS_LABELS,
  financeStatusSchema,
  type AdminEvent,
  type BudgetCategory,
  type BudgetCategoryTotals,
  type EventBudget,
  type FinanceEntry,
  type FinanceKind,
  type FinanceStatus,
} from '@growup/site-shared';
import {
  Button,
  Card,
  Checkbox,
  DateInput,
  Disclosure,
  Field,
  Input,
  Notice,
  Select,
  StatusBadge,
  Table,
  Textarea,
  type Column,
} from '@growup/ui';
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { z } from 'zod';

import { ApiError, download, getJson, messageOf, send, uploadDocument } from '../api';
import { formatDateTime } from '../format';
import { InvoiceThumbnail } from './InvoicePreview';
import { centsToInput, formatEuros, parseEuros } from '../store/labels';

const CATEGORIES = '/api/site/event-budget/categories';
const ACCEPT = 'application/pdf,image/jpeg,image/png';

const KIND_LABEL: Record<FinanceKind, { one: string; many: string; who: string }> = {
  cost: { one: 'custo', many: 'Custos', who: 'Fornecedor' },
  revenue: { one: 'receita', many: 'Receitas', who: 'Entidade' },
};

const formatDay = (day: string | null) =>
  day === null ? 'sem data' : day.split('-').reverse().join('/');

/**
 * Orcamento do evento (decidido pelo GrowuP a 20-10-2026, docs/eventos):
 * orcamento total e por categoria, custos e receitas Previstos ou Pagos, e as
 * faturas dos fornecedores. Fica aberto depois do evento: ha valores que so
 * chegam muito depois. Precisa de `events.budget`.
 */
export function BudgetView({ event }: { event: AdminEvent }) {
  const base = `/api/site/events/${event.id}/budget`;
  const [budget, setBudget] = useState<EventBudget | null>(null);
  const [categories, setCategories] = useState<BudgetCategory[]>([]);
  const [editing, setEditing] = useState<{ kind: FinanceKind; id: string | null } | null>(null);
  /** Se a fatura escolhida ao criar nao subiu, o editor do novo lancamento diz porque. */
  const [uploadProblem, setUploadProblem] = useState<string | null>(null);
  const [planning, setPlanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    Promise.all([
      getJson(base, eventBudgetSchema),
      getJson(CATEGORIES, z.array(budgetCategorySchema)),
    ])
      .then(([b, c]) => {
        setBudget(b);
        setCategories(c);
      })
      .catch((e: unknown) => setError(messageOf(e)));
  }, [base]);
  useEffect(load, [load]);

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      const res = await action();
      if (res !== null && res !== undefined) setBudget(eventBudgetSchema.parse(res));
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  if (budget === null) {
    return error ? <Notice tone="danger">{error}</Notice> : <p>A carregar…</p>;
  }

  if (editing !== null) {
    const entry = budget.entries.find((e) => e.id === editing.id) ?? null;
    return (
      <EntryEditor
        key={editing.id ?? `new-${editing.kind}`}
        base={base}
        kind={editing.kind}
        entry={entry}
        known={budget.entries.map((e) => e.id)}
        categories={categories}
        uploadProblem={uploadProblem}
        onSaved={(b, id, problem) => {
          setBudget(b);
          setUploadProblem(problem ?? null);
          setEditing({ kind: editing.kind, id });
        }}
        onClose={() => {
          setUploadProblem(null);
          setEditing(null);
        }}
      />
    );
  }

  const t = budget.totals;
  const closed = budget.accountsClosedAt !== null;
  const ended = new Date(event.endsAt) < new Date();
  const costs = budget.entries.filter((e) => e.kind === 'cost');
  const revenues = budget.entries.filter((e) => e.kind === 'revenue');

  return (
    <div className="flex flex-col gap-4">
      <Card
        headingLevel={3}
        title="Orçamento"
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() =>
                void download(`${base}/export.csv`, `contas_${event.slug}.csv`).catch(
                  (e: unknown) => setError(messageOf(e)),
                )
              }
            >
              Exportar CSV
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() =>
                void download(`${base}/documents.zip`, `faturas_${event.slug}.zip`).catch(
                  (e: unknown) => setError(messageOf(e)),
                )
              }
            >
              Faturas (ZIP)
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          {error ? <Notice tone="danger">{error}</Notice> : null}
          {closed ? (
            <Notice tone="success" title="Contas fechadas">
              Fechadas por {budget.accountsClosedBy ?? '—'} a{' '}
              {formatDateTime(budget.accountsClosedAt ?? '')}. Pode continuar a lançar valores; se
              chegar mais alguma coisa, reabra as contas.
            </Notice>
          ) : ended ? (
            <p className="text-gu-ink-muted">
              O evento já acabou, mas as contas ficam abertas enquanto faltar alguma coisa. Quando
              já não esperar mais nada, marque as contas como fechadas.
            </p>
          ) : null}
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
            <Kpi
              label="Orçamento"
              value={budget.budgetCents === null ? '—' : formatEuros(budget.budgetCents)}
            />
            <Kpi label="Pago" value={formatEuros(t.costSettledCents)} />
            <Kpi label="Previsto por pagar" value={formatEuros(t.costPlannedCents)} />
            <Kpi
              label="Disponível"
              value={t.availableCents === null ? '—' : formatEuros(t.availableCents)}
              tone={t.availableCents !== null && t.availableCents < 0 ? 'danger' : undefined}
              note={
                t.availableCents !== null && t.availableCents < 0
                  ? 'Acima do orçamento'
                  : budget.budgetCents === null
                    ? 'Sem orçamento definido'
                    : undefined
              }
            />
            <Kpi label="Receitas recebidas" value={formatEuros(t.revenueSettledCents)} />
            <Kpi label="Receitas previstas" value={formatEuros(t.revenuePlannedCents)} />
            <Kpi
              label="Saldo (recebido − pago)"
              value={formatEuros(t.balanceCents)}
              tone={t.balanceCents < 0 ? 'danger' : undefined}
            />
            <Kpi
              label="Faturas em falta"
              value={String(t.missingDocuments)}
              tone={t.missingDocuments > 0 ? 'warning' : undefined}
              note="Custos pagos sem fatura nem recibo"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={() => setPlanning((p) => !p)}>
              {planning ? 'Fechar orçamento' : 'Definir orçamento'}
            </Button>
            {closed ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => void run(() => send('POST', `${base}/reopen`))}
              >
                Reabrir contas
              </Button>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => void run(() => send('POST', `${base}/close`))}
              >
                Marcar contas fechadas
              </Button>
            )}
          </div>
          {planning ? (
            <PlanEditor
              base={base}
              budget={budget}
              categories={categories}
              onSaved={(b) => {
                setBudget(b);
                setPlanning(false);
              }}
            />
          ) : (
            <CategoryBars rows={budget.categories.filter((c) => c.kind === 'cost')} />
          )}
        </div>
      </Card>

      {(['cost', 'revenue'] as const).map((kind) => (
        <Card
          key={kind}
          headingLevel={3}
          title={KIND_LABEL[kind].many}
          actions={
            <Button size="sm" onClick={() => setEditing({ kind, id: null })}>
              {kind === 'cost' ? 'Novo custo' : 'Nova receita'}
            </Button>
          }
        >
          <Table
            caption={KIND_LABEL[kind].many}
            columns={entryColumns(kind)}
            rows={kind === 'cost' ? costs : revenues}
            rowKey={(e) => e.id}
            empty={kind === 'cost' ? 'Ainda não há custos.' : 'Ainda não há receitas.'}
            onRowClick={(e) => setEditing({ kind, id: e.id })}
          />
        </Card>
      ))}

      <CategoriesEditor categories={categories} onChanged={setCategories} />
    </div>
  );
}

function Kpi({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string;
  note?: string | undefined;
  tone?: 'danger' | 'warning' | undefined;
}) {
  const color =
    tone === 'danger' ? 'text-gu-danger' : tone === 'warning' ? 'text-gu-warning' : 'text-gu-ink';
  return (
    <div className="flex flex-col gap-1 border-2 border-gu-line p-3">
      <span className="text-caption font-semibold uppercase tracking-wider text-gu-ink-muted">
        {label}
      </span>
      <span className={`font-display text-title ${color}`}>{value}</span>
      {note ? <span className="text-caption text-gu-ink-muted">{note}</span> : null}
    </div>
  );
}

/** Gasto e previsto contra o orcamentado, por categoria de custos. */
function CategoryBars({ rows }: { rows: BudgetCategoryTotals[] }) {
  const used = rows.filter((r) => r.budgetCents !== null || r.settledCents + r.plannedCents > 0);
  if (used.length === 0) {
    return <p className="text-gu-ink-muted">Sem valores por categoria.</p>;
  }
  return (
    <ul className="flex flex-col gap-2" aria-label="Por categoria">
      {used.map((r) => {
        const spent = r.settledCents + r.plannedCents;
        const over = r.budgetCents !== null && spent > r.budgetCents;
        const scale = Math.max(r.budgetCents ?? 0, spent, 1);
        return (
          <li key={r.categoryId} className="grid items-center gap-2 sm:grid-cols-[10rem_1fr_14rem]">
            <span>{r.name}</span>
            <span className="flex h-2 bg-gu-sunken" aria-hidden="true">
              <span
                className="bg-gu-success"
                style={{ width: `${String((r.settledCents / scale) * 100)}%` }}
              />
              <span
                className="bg-gu-warning"
                style={{ width: `${String((r.plannedCents / scale) * 100)}%` }}
              />
            </span>
            <span className={`text-right tabular-nums ${over ? 'text-gu-danger' : ''}`}>
              {formatEuros(spent)} / {r.budgetCents === null ? '—' : formatEuros(r.budgetCents)}
              {over ? ' · acima' : ''}
            </span>
          </li>
        );
      })}
      <li className="text-caption text-gu-ink-muted">Verde: pago. Amarelo: previsto.</li>
    </ul>
  );
}

function PlanEditor({
  base,
  budget,
  categories,
  onSaved,
}: {
  base: string;
  budget: EventBudget;
  categories: BudgetCategory[];
  onSaved: (b: EventBudget) => void;
}) {
  const costCategories = categories.filter(
    (c) =>
      c.kind === 'cost' &&
      (c.active || budget.categories.some((t) => t.categoryId === c.id && t.budgetCents !== null)),
  );
  const [total, setTotal] = useState(
    budget.budgetCents === null ? '' : centsToInput(budget.budgetCents),
  );
  const [lines, setLines] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      budget.categories
        .filter((c) => c.budgetCents !== null)
        .map((c) => [c.categoryId, centsToInput(c.budgetCents ?? 0)]),
    ),
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const budgetCents = total.trim() === '' ? null : parseEuros(total);
    if (budgetCents === null && total.trim() !== '') {
      setError('O orçamento total não é um valor válido.');
      return;
    }
    const parsed = Object.entries(lines)
      .filter(([, v]) => v.trim() !== '')
      .map(([categoryId, v]) => ({ categoryId, amountCents: parseEuros(v) }));
    if (parsed.some((l) => l.amountCents === null)) {
      setError('Há um valor por categoria que não é válido.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      onSaved(eventBudgetSchema.parse(await send('PUT', base, { budgetCents, lines: parsed })));
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="flex flex-col gap-3" onSubmit={(e) => void submit(e)} noValidate>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <Field label="Orçamento total (€)" optional hint="Vazio = sem orçamento.">
        <Input inputMode="decimal" value={total} onChange={(e) => setTotal(e.target.value)} />
      </Field>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {costCategories.map((c) => (
          <Field key={c.id} label={`${c.name} (€)`} optional>
            <Input
              inputMode="decimal"
              value={lines[c.id] ?? ''}
              onChange={(e) => setLines((l) => ({ ...l, [c.id]: e.target.value }))}
            />
          </Field>
        ))}
      </div>
      <div>
        <Button type="submit" disabled={busy}>
          Guardar orçamento
        </Button>
      </div>
    </form>
  );
}

function entryColumns(kind: FinanceKind): Column<FinanceEntry>[] {
  return [
    {
      key: 'description',
      header: kind === 'cost' ? 'Custo' : 'Receita',
      cell: (e) => (
        <span className="flex flex-col">
          <span className="font-semibold">{e.description}</span>
          <span className="text-caption text-gu-ink-muted">
            {[e.counterparty, formatDay(e.entryDate)].filter(Boolean).join(' · ')}
          </span>
        </span>
      ),
    },
    { key: 'category', header: 'Categoria', hideOnMobile: true, cell: (e) => e.categoryName },
    {
      key: 'amount',
      header: 'Valor',
      align: 'right',
      cell: (e) => <span className="tabular-nums">{formatEuros(e.amountCents)}</span>,
    },
    {
      key: 'status',
      header: 'Estado',
      cell: (e) => (
        <StatusBadge tone={e.status === 'settled' ? 'success' : 'warning'}>
          {FINANCE_STATUS_LABELS[e.kind][e.status]}
        </StatusBadge>
      ),
    },
    {
      key: 'documents',
      header: 'Fatura',
      hideOnMobile: true,
      cell: (e) =>
        e.documents.length > 0 ? (
          `📎 ${String(e.documents.length)}`
        ) : e.kind === 'cost' && e.status === 'settled' ? (
          <StatusBadge tone="danger">Falta fatura</StatusBadge>
        ) : (
          '—'
        ),
    },
  ];
}

function EntryEditor({
  base,
  kind,
  entry,
  known,
  categories,
  uploadProblem,
  onSaved,
  onClose,
}: {
  base: string;
  kind: FinanceKind;
  entry: FinanceEntry | null;
  /** Os lancamentos que ja existiam: o novo e o que nao esta aqui. */
  known: string[];
  categories: BudgetCategory[];
  uploadProblem: string | null;
  /** `problem`: a fatura escolhida no "Novo custo" nao subiu, mas o lancamento ficou criado. */
  onSaved: (b: EventBudget, id: string, problem?: string) => void;
  onClose: () => void;
}) {
  const options = categories.filter(
    (c) => c.kind === kind && (c.active || c.id === entry?.categoryId),
  );
  const [description, setDescription] = useState(entry?.description ?? '');
  const [categoryId, setCategoryId] = useState(entry?.categoryId ?? options[0]?.id ?? '');
  const [counterparty, setCounterparty] = useState(entry?.counterparty ?? '');
  const [entryDate, setEntryDate] = useState(entry?.entryDate ?? '');
  const [amount, setAmount] = useState(entry ? centsToInput(entry.amountCents) : '');
  const [vat, setVat] = useState(entry?.vatCents == null ? '' : centsToInput(entry.vatCents));
  const [status, setStatus] = useState<FinanceStatus>(entry?.status ?? 'planned');
  const [notes, setNotes] = useState(entry?.notes ?? '');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(uploadProblem);
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** So no "Novo custo": a fatura sobe logo a seguir a criar (F-015). */
  const [invoice, setInvoice] = useState<File | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const newInvoiceInput = useRef<HTMLInputElement>(null);

  const label = KIND_LABEL[kind];

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const amountCents = parseEuros(amount);
    const vatCents = vat.trim() === '' ? null : parseEuros(vat);
    const problems: Record<string, string> = {};
    if (amountCents === null || amountCents === 0)
      problems.amountCents = 'Escreva o valor, ex.: 12,50.';
    if (vat.trim() !== '' && vatCents === null) problems.vatCents = 'Escreva o IVA, ex.: 2,34.';
    if (Object.keys(problems).length > 0) {
      setFields(problems);
      return;
    }
    const payload = {
      kind,
      categoryId,
      description,
      counterparty,
      entryDate: entryDate === '' ? null : entryDate,
      amountCents,
      vatCents,
      status,
      notes,
    };
    setBusy(true);
    setError(null);
    setSaved(null);
    setFields({});
    try {
      const res = eventBudgetSchema.parse(
        entry
          ? await send('PUT', `${base}/entries/${entry.id}`, payload)
          : await send('POST', `${base}/entries`, payload),
      );
      const id = entry?.id ?? res.entries.find((x) => !known.includes(x.id))?.id ?? null;
      setSaved(entry ? 'Guardado.' : 'Criado. Já pode juntar a fatura.');
      if (id && !entry && invoice) {
        try {
          const withInvoice = eventBudgetSchema.parse(
            await uploadDocument(`${base}/entries/${id}/documents`, invoice),
          );
          onSaved(withInvoice, id);
        } catch (err) {
          onSaved(res, id, `Criado, mas a fatura não foi carregada: ${messageOf(err)}`);
        }
      } else if (id) onSaved(res, id);
      else onClose();
    } catch (err) {
      setError(messageOf(err));
      if (err instanceof ApiError) setFields(err.fields);
    } finally {
      setBusy(false);
    }
  };

  const act = async (action: () => Promise<unknown>, after?: (b: EventBudget) => void) => {
    if (!entry) return;
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      const b = eventBudgetSchema.parse(await action());
      if (after) after(b);
      else onSaved(b, entry.id);
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  const upload = (file: File | undefined) => {
    if (!file || !entry) return;
    void act(() => uploadDocument(`${base}/entries/${entry.id}/documents`, file));
    if (fileInput.current) fileInput.current.value = '';
  };

  const remove = () => {
    if (!entry) return;
    if (!window.confirm(`Apagar «${entry.description}» das contas? Fica registado quem apagou.`)) {
      return;
    }
    void act(
      () => send('DELETE', `${base}/entries/${entry.id}`),
      () => onClose(),
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          ← Orçamento
        </Button>
      </div>
      <Card
        headingLevel={3}
        title={entry ? entry.description : kind === 'cost' ? 'Novo custo' : 'Nova receita'}
      >
        <form className="flex flex-col gap-4" onSubmit={(e) => void submit(e)} noValidate>
          {error ? <Notice tone="danger">{error}</Notice> : null}
          {saved ? <Notice tone="success">{saved}</Notice> : null}
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Descrição" error={fields.description} className="sm:col-span-2">
              <Input
                value={description}
                maxLength={140}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Field>
            <Field label="Categoria" error={fields.categoryId}>
              <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                {options.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={label.who} optional error={fields.counterparty}>
              <Input
                value={counterparty}
                maxLength={120}
                onChange={(e) => setCounterparty(e.target.value)}
              />
            </Field>
            <Field label="Valor com IVA (€)" error={fields.amountCents}>
              <Input
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </Field>
            <Field
              label="IVA (€)"
              optional
              hint="Só se quiser o IVA à parte."
              error={fields.vatCents}
            >
              <Input inputMode="decimal" value={vat} onChange={(e) => setVat(e.target.value)} />
            </Field>
            <Field label="Estado" error={fields.status}>
              <Select
                value={status}
                onChange={(e) => setStatus(financeStatusSchema.parse(e.target.value))}
              >
                <option value="planned">{FINANCE_STATUS_LABELS[kind].planned}</option>
                <option value="settled">{FINANCE_STATUS_LABELS[kind].settled}</option>
              </Select>
            </Field>
            <Field
              label="Data"
              optional
              hint={kind === 'cost' ? 'Da fatura ou do pagamento.' : 'Do recebimento.'}
              error={fields.entryDate}
            >
              <DateInput value={entryDate} onChange={setEntryDate} />
            </Field>
            <Field label="Notas" optional error={fields.notes} className="sm:col-span-2">
              <Textarea
                value={notes}
                maxLength={2000}
                rows={3}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Field>
          </div>
          {!entry && kind === 'cost' ? (
            <div className="flex flex-col gap-2">
              {/* O botao do browser vem na lingua do sistema; este fica sempre em portugues. */}
              <input
                ref={newInvoiceInput}
                type="file"
                accept={ACCEPT}
                className="sr-only"
                tabIndex={-1}
                aria-hidden="true"
                data-testid="nova-fatura"
                onChange={(e) => setInvoice(e.target.files?.[0] ?? null)}
              />
              <Field
                label="Fatura"
                optional
                hint="PDF, JPG ou PNG até 10 MB. No telemóvel pode tirar uma foto."
              >
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="self-start"
                  disabled={busy}
                  onClick={() => newInvoiceInput.current?.click()}
                >
                  {invoice ? 'Trocar fatura' : 'Escolher fatura'}
                </Button>
              </Field>
              {invoice ? (
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <span className="min-w-0 break-all">{invoice.name}</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => {
                      setInvoice(null);
                      if (newInvoiceInput.current) newInvoiceInput.current.value = '';
                    }}
                  >
                    Tirar
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={busy}>
              {entry ? 'Guardar' : `Criar ${label.one}`}
            </Button>
            {entry ? (
              <Button type="button" variant="danger" disabled={busy} onClick={remove}>
                Apagar
              </Button>
            ) : null}
          </div>
        </form>
      </Card>

      {entry ? (
        <Card headingLevel={3} title="Faturas e recibos">
          <div className="flex flex-col gap-3">
            <p className="text-gu-ink-muted">
              PDF, JPG ou PNG até 10 MB. Ficam privados: só quem gere o orçamento dos eventos os
              abre.
            </p>
            {entry.documents.length === 0 ? <p>Ainda sem documentos.</p> : null}
            <ul className="flex flex-col gap-2">
              {entry.documents.map((d) => (
                <li
                  key={d.id}
                  className="flex flex-wrap items-center gap-3 border-2 border-gu-line p-2"
                >
                  <InvoiceThumbnail
                    path={`${base}/documents/${d.id}`}
                    document={d}
                    onError={setError}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold">{d.originalName || 'documento'}</span>
                    <span className="text-caption text-gu-ink-muted">
                      {(d.bytes / 1024).toFixed(0)} kB · {d.uploadedBy ?? '—'} ·{' '}
                      {formatDateTime(d.uploadedAt)}
                    </span>
                  </span>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      void download(
                        `${base}/documents/${d.id}`,
                        d.originalName || 'documento',
                      ).catch((e: unknown) => setError(messageOf(e)))
                    }
                  >
                    Descarregar
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => {
                      if (
                        window.confirm('Retirar este documento? Fica registado quem o retirou.')
                      ) {
                        void act(() => send('DELETE', `${base}/documents/${d.id}`));
                      }
                    }}
                  >
                    Retirar
                  </Button>
                </li>
              ))}
            </ul>
            <div>
              {/* O botao do browser vem na lingua do sistema; este fica sempre em portugues. */}
              <input
                ref={fileInput}
                type="file"
                accept={ACCEPT}
                className="sr-only"
                tabIndex={-1}
                aria-hidden="true"
                onChange={(e) => upload(e.target.files?.[0])}
              />
              <Button
                size="sm"
                variant="secondary"
                disabled={busy}
                onClick={() => fileInput.current?.click()}
              >
                Juntar fatura ou recibo
              </Button>
            </div>
          </div>
        </Card>
      ) : null}
    </div>
  );
}

/** As categorias sao comuns a todos os eventos. */
function CategoriesEditor({
  categories,
  onChanged,
}: {
  categories: BudgetCategory[];
  onChanged: (c: BudgetCategory[]) => void;
}) {
  const [name, setName] = useState('');
  const [kind, setKind] = useState<FinanceKind>('cost');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      const saved = budgetCategorySchema.parse(await action());
      onChanged(
        categories.some((c) => c.id === saved.id)
          ? categories.map((c) => (c.id === saved.id ? saved : c))
          : [...categories, saved],
      );
      return true;
    } catch (e) {
      setError(messageOf(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <Disclosure summary="Categorias (comuns a todos os eventos)">
      <div className="flex flex-col gap-3 pt-3">
        {error ? <Notice tone="danger">{error}</Notice> : null}
        {(['cost', 'revenue'] as const).map((k) => (
          <div key={k} className="flex flex-col gap-2">
            <h4 className="font-semibold">{KIND_LABEL[k].many}</h4>
            <ul className="flex flex-wrap gap-3">
              {categories
                .filter((c) => c.kind === k)
                .map((c) => (
                  <li key={c.id}>
                    <Checkbox
                      label={c.name}
                      checked={c.active}
                      disabled={busy}
                      onChange={(e) =>
                        void save(() =>
                          send('PUT', `${CATEGORIES}/${c.id}`, {
                            kind: c.kind,
                            name: c.name,
                            active: e.target.checked,
                          }),
                        )
                      }
                    />
                  </li>
                ))}
            </ul>
          </div>
        ))}
        <p className="text-caption text-gu-ink-muted">
          Desmarcada, a categoria deixa de aparecer para escolher; os valores antigos ficam com ela.
        </p>
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void save(() => send('POST', CATEGORIES, { kind, name })).then((ok) => {
              if (ok) setName('');
            });
          }}
        >
          <Field label="Nova categoria">
            <Input value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Tipo">
            <Select
              value={kind}
              onChange={(e) => setKind(e.target.value === 'revenue' ? 'revenue' : 'cost')}
            >
              <option value="cost">Custo</option>
              <option value="revenue">Receita</option>
            </Select>
          </Field>
          <Button type="submit" size="sm" disabled={busy || name.trim() === ''}>
            Acrescentar
          </Button>
        </form>
      </div>
    </Disclosure>
  );
}
